import { z } from 'zod'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import axios, { endpoints } from '@/lib/axios'
import { Loader2, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { Textarea } from '@/components/ui/textarea'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FeedPrivacy,
  ShareDestination,
  type SharePayload,
  ShareType,
} from '@/types/feed.type'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'

// Schema validation
export const shareSchema = z.object({
  shareType: z.enum(['direct', 'with_comment', 'repost']),
  destination: z.enum(['feed', 'message', 'external']),
  shareComment: z.string().max(500).optional(),
  recipientUserId: z.string().optional(),
  recipientUserName: z.string().optional(),
  externalPlatform: z.string().optional(),
  externalUrl: z.string().url().optional(),
  privacy: z.enum(['public', 'friends', 'private']).optional(),
})

type ShareFormValues = z.infer<typeof shareSchema>

interface FeedShareFormDialogProps {
  feedId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

const FeedShareFormDialog: React.FC<FeedShareFormDialogProps> = ({
  feedId,
  onSuccess,
  onError,
}) => {
  const isSubmitting = useBoolean()
  const [shareType, setShareType] = React.useState<ShareType>(ShareType.REPOST)
  const [destination, setDestination] = React.useState<ShareDestination>(
    ShareDestination.FEED,
  )

  const form = useForm<ShareFormValues>({
    defaultValues: {
      shareType: ShareType.REPOST,
      destination: ShareDestination.FEED,
      shareComment: '',
      recipientUserId: '',
      recipientUserName: '',
      // externalPlatform: '',
      // externalUrl: '',
      privacy: FeedPrivacy.PUBLIC,
    },
  })

  const onSubmit = async (values: ShareFormValues) => {
    if (isSubmitting.value) return

    try {
      isSubmitting.onTrue()

      const payload: SharePayload = {
        shareType: values.shareType as ShareType,
        destination: values.destination as ShareDestination,
        ...(values.shareComment && { shareComment: values.shareComment }),
        ...(values.recipientUserId && {
          recipientUserId: values.recipientUserId,
        }),
        ...(values.recipientUserName && {
          recipientUserName: values.recipientUserName,
        }),
        ...(values.externalPlatform && {
          externalPlatform: values.externalPlatform,
        }),
        ...(values.externalUrl && { externalUrl: values.externalUrl }),
        ...(values.privacy && { privacy: values.privacy as FeedPrivacy }),
      }

      await axios.post(endpoints.feeds.shareById(feedId), payload)

      onSuccess?.()
      form.reset()
    } catch (error) {
      onError?.(error as Error)
      console.error('Error sharing feed:', error)
    } finally {
      isSubmitting.onFalse()
    }
  }

  // const handleShareTypeChange = (type: string) => {
  //   const newShareType = type as ShareType
  //   setShareType(newShareType)
  //   form.setValue('shareType', newShareType)
  // }

  // const handleDestinationChange = (dest: string) => {
  //   const newDestination = dest as ShareDestination
  //   setDestination(newDestination)
  //   form.setValue('destination', newDestination)
  // }

  useEffect(() => {
    setShareType(ShareType.REPOST)
    setDestination(ShareDestination.FEED)
  }, [])

  // const shareTypeOptions = [
  //   { value: 'direct', label: 'แชร์โดยตรง', icon: Share2 },
  //   {
  //     value: 'with_comment',
  //     label: 'แชร์พร้อมความคิดเห็น',
  //     icon: MessageCircle,
  //   },
  //   { value: 'repost', label: 'รีโพสต์', icon: Repeat },
  // ]

  // const destinationOptions = [
  //   { value: 'feed', label: 'ฟีด', icon: Globe },
  //   { value: 'message', label: 'ข้อความส่วนตัว', icon: Send },
  //   { value: 'external', label: 'แพลตฟอร์มภายนอก', icon: ExternalLink },
  // ]

  // const privacyOptions = [
  //   { value: 'public', label: 'สาธารณะ', icon: Globe },
  //   { value: 'friends', label: 'เฉพาะเพื่อน', icon: Users },
  //   { value: 'private', label: 'ส่วนตัว', icon: Lock },
  // ]

  return (
    <DialogContent aria-describedby="" className="p-0 max-w-lg w-[95dvw]">
      <DialogHeader className="p-6 pb-0">
        <DialogTitle className="truncate w-[calc(100dvw-100px)] max-w-[455px]">
          แชร์โพสต์
        </DialogTitle>
      </DialogHeader>
      <div className="p-6 pt-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Share Type Selection */}
            {/* <FormField
              control={form.control}
              name="shareType"
              render={() => (
                <FormItem>
                  <FormLabel className="text-white">ประเภทการแชร์</FormLabel>
                  <FormControl>
                    <Tabs
                      value={shareType}
                      onValueChange={handleShareTypeChange}
                      className="w-full"
                    >
                      <TabsList className="grid w-full grid-cols-3">
                        {shareTypeOptions.map((option) => {
                          const Icon = option.icon
                          return (
                            <TabsTrigger
                              key={option.value}
                              value={option.value}
                              className="flex items-center gap-1 text-xs"
                            >
                              <Icon className="w-3 h-3" />
                              {option.label}
                            </TabsTrigger>
                          )
                        })}
                      </TabsList>
                    </Tabs>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            {/* Destination Selection */}
            {/* <FormField
              control={form.control}
              name="destination"
              render={() => (
                <FormItem>
                  <FormLabel className="text-white">ปลายทาง</FormLabel>
                  <FormControl>
                    <Select
                      value={destination}
                      onValueChange={handleDestinationChange}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                        <SelectValue placeholder="เลือกปลายทาง" />
                      </SelectTrigger>
                      <SelectContent>
                        {destinationOptions.map((option) => {
                          const Icon = option.icon
                          return (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {option.label}
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            {/* Share Comment (if with_comment or repost) */}
            {(shareType === 'with_comment' || shareType === 'repost') && (
              <FormField
                control={form.control}
                name="shareComment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">
                      {shareType === 'with_comment'
                        ? 'ความคิดเห็น'
                        : 'ข้อความประกอบรีโพสต์'}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Textarea
                          maxLength={500}
                          placeholder={
                            shareType === 'with_comment'
                              ? 'เพิ่มความคิดเห็นของคุณ...'
                              : 'เพิ่มข้อความประกอบรีโพสต์...'
                          }
                          className="min-h-[80px] resize-none bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all break-all"
                          {...field}
                        />
                        <div className="absolute -bottom-[22px] right-3 text-xs text-gray-500">
                          {field.value?.length || 0}/500
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Recipient Fields (if destination is message) */}
            {destination === 'message' && (
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="recipientUserName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700">
                        ชื่อผู้รับ
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="ค้นหาชื่อผู้ใช้..."
                          className="bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recipientUserId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700">ID ผู้รับ</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="ID ของผู้รับ"
                          className="bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* External Platform Fields (if destination is external) */}
            {destination === 'external' && (
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="externalPlatform"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700">แพลตฟอร์ม</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="เช่น Facebook, Twitter, Line..."
                          className="bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="externalUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700">
                        URL ปลายทาง
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://..."
                          className="bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Privacy Settings (if destination is feed) */}
            {/* {destination === 'feed' && (
              <FormField
                control={form.control}
                name="privacy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      ความเป็นส่วนตัว
                    </FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                          <SelectValue placeholder="เลือกระดับความเป็นส่วนตัว" />
                        </SelectTrigger>
                        <SelectContent>
                          {privacyOptions.map((option) => {
                            const Icon = option.icon
                            return (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  {option.label}
                                </div>
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )} */}

            <div className="flex items-center justify-between pt-4">
              <div className="flex space-x-2 text-muted"></div>
              <Button
                type="submit"
                disabled={isSubmitting.value}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {isSubmitting.value ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    แชร์โพสต์
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </DialogContent>
  )
}

export default FeedShareFormDialog
